/**
 * Feature Flag Hook for Frontend
 *
 * Provides feature flag checking capabilities that mirror the backend
 * feature flag system. This ensures consistent feature gating across
 * the entire application.
 *
 * @example
 * const { hasFeature, getLimit } = useFeatures();
 *
 * if (hasFeature('custom_short_codes')) {
 *   // Show custom code input
 * }
 *
 * const maxShares = getLimit('link_sharing'); // 25 for Plus, null for Pro
 */

import { useAuth, type SubscriptionTier } from './use-auth';

// =============================================================================
// Feature Types (mirrored from backend)
// =============================================================================

/**
 * All feature flags in the system.
 * Keep in sync with workers/src/features/types.ts
 */
export type FeatureFlag =
  // Link features (Plus)
  | 'custom_short_codes'
  | 'link_folders'
  | 'link_expiration'
  | 'link_password_protection'
  // Usage limits
  | 'monthly_link_creation'
  // Resource hierarchy - project limits
  | 'project_limits'
  // Analytics features (Plus)
  | 'extended_analytics_history'
  | 'conversion_tracking'
  | 'export_analytics'
  // QR features (Plus/Pro)
  | 'branded_qr_codes'
  | 'custom_qr_colors'
  | 'qr_custom_logo'
  // Form features
  | 'form_creation'
  | 'form_submissions_monthly'
  | 'form_fields'
  | 'form_advanced_fields'
  | 'form_conditional_logic'
  | 'form_export'
  | 'form_webhooks'
  // Collaboration (Plus/Pro)
  | 'link_sharing'
  | 'api_access'
  // Pro-only features
  | 'custom_link_previews'
  | 'team_members'
  | 'webhook_notifications'
  | 'advanced_routing'
  | 'white_label';

/**
 * Feature definition with metadata
 */
interface FeatureDefinition {
  minTier: SubscriptionTier;
  limits?: Partial<Record<SubscriptionTier, number | null>>;
  beta?: boolean;
}

// =============================================================================
// Feature Definitions (mirrored from backend)
// =============================================================================

/**
 * Tier hierarchy for comparison
 */
const TIER_HIERARCHY: Record<SubscriptionTier, number> = {
  free: 0,
  plus: 1,
  pro: 2,
};

/**
 * Feature definitions - keep in sync with workers/src/features/definitions.ts
 * This is a subset focusing on limits and tier requirements
 */
const FEATURES: Record<FeatureFlag, FeatureDefinition> = {
  // Link features
  custom_short_codes: { minTier: 'plus' },
  link_folders: { minTier: 'plus' },
  link_expiration: { minTier: 'free' },
  link_password_protection: { minTier: 'plus' },

  // Usage limits
  monthly_link_creation: {
    minTier: 'free',
    limits: { free: 50, plus: 500, pro: 5000 },
  },

  // Resource hierarchy - project limits
  project_limits: {
    minTier: 'plus', // Free users get 1 project, Plus gets 10, Pro unlimited
    limits: { free: 1, plus: 10, pro: null },
  },

  // Analytics features
  extended_analytics_history: {
    minTier: 'plus',
    limits: { free: 30, plus: 365, pro: null },
  },
  conversion_tracking: { minTier: 'plus' },
  export_analytics: { minTier: 'plus' },

  // QR features
  branded_qr_codes: { minTier: 'plus' }, // Shape customization, remove watermark
  custom_qr_colors: { minTier: 'free' },
  qr_custom_logo: { minTier: 'pro' }, // Upload custom logo

  // Form features
  form_creation: {
    minTier: 'free',
    limits: { free: 3, plus: 25, pro: null },
  },
  form_submissions_monthly: {
    minTier: 'free',
    limits: { free: 100, plus: 2500, pro: null },
  },
  form_fields: {
    minTier: 'free',
    limits: { free: 10, plus: 25, pro: null },
  },
  form_advanced_fields: { minTier: 'plus' },
  form_conditional_logic: { minTier: 'plus' },
  form_export: { minTier: 'plus' },
  form_webhooks: { minTier: 'pro', beta: true },

  // Collaboration
  link_sharing: {
    minTier: 'plus',
    limits: { free: 0, plus: 25, pro: null },
  },
  api_access: { minTier: 'pro' },

  // Pro-only features
  custom_link_previews: { minTier: 'pro' },
  team_members: {
    minTier: 'pro',
    limits: { pro: 10 },
  },
  webhook_notifications: { minTier: 'pro', beta: true },
  advanced_routing: { minTier: 'pro', beta: true },
  white_label: { minTier: 'pro' },
};

/**
 * Tier display names for UI
 */
export const TIER_NAMES: Record<SubscriptionTier, string> = {
  free: 'Free',
  plus: 'Plus',
  pro: 'Pro',
};

/**
 * Tier pricing for UI display
 * Note: Monthly prices are for display only (annual billing only during beta)
 */
export const TIER_PRICING: Record<
  SubscriptionTier,
  { monthly: number | null; yearly: number | null }
> = {
  free: { monthly: null, yearly: null },
  plus: { monthly: 5, yearly: 60 }, // $60/year = $5/mo equivalent
  pro: { monthly: 10, yearly: 120 }, // $120/year = $10/mo equivalent
};

// =============================================================================
// Hook Implementation
// =============================================================================

/**
 * Feature flags hook
 *
 * Provides methods to check feature access and get limits based on user's tier.
 */
export function useFeatures() {
  const { tier, isPlusUser, isProUser } = useAuth();

  /**
   * Check if current user has access to a feature
   */
  const hasFeature = (flag: FeatureFlag): boolean => {
    const definition = FEATURES[flag];
    if (!definition) return false;

    const userTierLevel = TIER_HIERARCHY[tier];
    const requiredTierLevel = TIER_HIERARCHY[definition.minTier];

    return userTierLevel >= requiredTierLevel;
  };

  /**
   * Check if user has a specific tier or higher
   */
  const hasTier = (requiredTier: SubscriptionTier): boolean => {
    return TIER_HIERARCHY[tier] >= TIER_HIERARCHY[requiredTier];
  };

  /**
   * Get feature limit for current user
   * Returns null if unlimited
   */
  const getLimit = (flag: FeatureFlag): number | null => {
    const definition = FEATURES[flag];
    if (!definition?.limits) return null;

    return definition.limits[tier] ?? null;
  };

  /**
   * Check if a feature is in beta
   */
  const isBeta = (flag: FeatureFlag): boolean => {
    return FEATURES[flag]?.beta ?? false;
  };

  /**
   * Get the minimum tier required for a feature
   */
  const getRequiredTier = (flag: FeatureFlag): SubscriptionTier => {
    return FEATURES[flag]?.minTier ?? 'free';
  };

  /**
   * Get all features available to current user's tier
   */
  const getAvailableFeatures = (): FeatureFlag[] => {
    const userTierLevel = TIER_HIERARCHY[tier];
    return (Object.keys(FEATURES) as FeatureFlag[]).filter(
      (flag) => TIER_HIERARCHY[FEATURES[flag].minTier] <= userTierLevel
    );
  };

  return {
    // Current user's tier info
    tier,
    tierName: TIER_NAMES[tier],
    isPlusUser,
    isProUser,

    // Feature checking
    hasFeature,
    hasTier,
    getLimit,
    isBeta,
    getRequiredTier,
    getAvailableFeatures,

    // Static data for UI
    allTiers: ['free', 'plus', 'pro'] as SubscriptionTier[],
    tierNames: TIER_NAMES,
    tierPricing: TIER_PRICING,
  };
}

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Get upgrade message for a feature
 */
export function getUpgradeMessage(flag: FeatureFlag): string {
  const requiredTier = FEATURES[flag]?.minTier ?? 'plus';
  const tierName = TIER_NAMES[requiredTier];

  const messages: Partial<Record<FeatureFlag, string>> = {
    custom_short_codes: `Upgrade to ${tierName} to create custom vanity URLs.`,
    link_folders: `Upgrade to ${tierName} to organize your links with folders.`,
    link_password_protection: `Upgrade to ${tierName} to password protect your links.`,
    project_limits: `Upgrade to ${tierName} to organize links into projects.`,
    monthly_link_creation: `Upgrade to ${tierName} to create more links per month.`,
    branded_qr_codes: `Upgrade to ${tierName} to customize QR code shapes and remove the watermark.`,
    qr_custom_logo: `Upgrade to ${tierName} to upload your own logo to QR codes.`,
    link_sharing: `Upgrade to ${tierName} to share analytics with your team.`,
    api_access: `Upgrade to ${tierName} for API access.`,
    custom_link_previews: `Upgrade to ${tierName} to customize how your links appear on social media.`,
    team_members: `Upgrade to ${tierName} to invite team members.`,
    conversion_tracking: `Upgrade to ${tierName} to track conversions.`,
    export_analytics: `Upgrade to ${tierName} to export analytics data.`,
    webhook_notifications: `Upgrade to ${tierName} to receive webhook notifications.`,
    advanced_routing: `Upgrade to ${tierName} for advanced routing features.`,
    white_label: `Upgrade to ${tierName} to remove all branding.`,
    // Form features
    form_creation: `Upgrade to ${tierName} to create more forms.`,
    form_submissions_monthly: `Upgrade to ${tierName} for more monthly submissions.`,
    form_fields: `Upgrade to ${tierName} to add more fields to your forms.`,
    form_advanced_fields: `Upgrade to ${tierName} to use checkbox, radio, number, and date fields.`,
    form_conditional_logic: `Upgrade to ${tierName} to show/hide fields based on responses.`,
    form_export: `Upgrade to ${tierName} to export form submissions as CSV.`,
    form_webhooks: `Upgrade to ${tierName} to receive form submissions via webhooks.`,
  };

  return messages[flag] ?? `Upgrade to ${tierName} to access this feature.`;
}
